/**
 * Bridge helpers for the built-in AI consultant widget (vapi-widget).
 * The platform widget does not expose an official "send message" API, so we
 * open it and best-effort inject the visitor's question into its input.
 * If injection is not possible, the chat still opens normally.
 */

type AnyRecord = Record<string, unknown>;
type RootLike = Document | ShadowRoot;

function safeQueryAll<T extends Element>(root: RootLike, selector: string): T[] {
  try {
    return Array.from(root.querySelectorAll<T>(selector));
  } catch {
    return [];
  }
}

/** Collect document + every reachable shadow root (widget may render in shadow DOM). */
function collectRoots(): RootLike[] {
  const roots: RootLike[] = [document];
  const visited = new Set<RootLike>([document]);

  const walk = (root: RootLike) => {
    const all = safeQueryAll<HTMLElement>(root, "*");
    all.forEach((el) => {
      const shadow = el.shadowRoot;
      if (shadow && !visited.has(shadow)) {
        visited.add(shadow);
        roots.push(shadow);
        walk(shadow);
      }
    });
  };

  try {
    walk(document);
  } catch {
    /* ignore */
  }
  return roots;
}

function isVisible(el: HTMLElement): boolean {
  try {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  } catch {
    return false;
  }
}

function findInput(): HTMLTextAreaElement | HTMLInputElement | null {
  const roots = collectRoots();
  const textareaSelectors = [
    "textarea",
    "input[type='text']",
    "input[type='search']",
    "input:not([type])",
    "[contenteditable='true']",
  ];

  for (const root of roots) {
    for (const selector of textareaSelectors) {
      const found = safeQueryAll<HTMLElement>(root, selector).find((el) => isVisible(el));
      if (found) return found as HTMLTextAreaElement | HTMLInputElement;
    }
  }
  return null;
}

function setNativeValue(el: HTMLTextAreaElement | HTMLInputElement, value: string) {
  const proto = el instanceof HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  if (descriptor?.set) {
    descriptor.set.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
  el.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
}

function findSendButton(): HTMLElement | null {
  const roots = collectRoots();
  const selectors = [
    "button[type='submit']",
    "button[aria-label*='send' i]",
    "button[aria-label*='Send' i]",
    "button[data-testid*='send' i]",
    "[class*='send' i] button",
    "button[title*='send' i]",
  ];
  for (const root of roots) {
    for (const selector of selectors) {
      const found = safeQueryAll<HTMLElement>(root, selector).find((el) => isVisible(el));
      if (found) return found;
    }
  }
  return null;
}

function dispatchEnter(el: HTMLElement) {
  const init = {
    key: "Enter",
    code: "Enter",
    keyCode: 13,
    which: 13,
    bubbles: true,
    composed: true,
    cancelable: true,
  };
  el.dispatchEvent(new KeyboardEvent("keydown", init as never));
  el.dispatchEvent(new KeyboardEvent("keypress", init as never));
  el.dispatchEvent(new KeyboardEvent("keyup", init as never));
}

function tryNativeApi(question: string): boolean {
  try {
    const win = window as unknown as AnyRecord;
    const host = safeQueryAll<HTMLElement>(document, "vapi-widget")[0] as unknown as AnyRecord | undefined;
    const candidates = [host, win.vapi, win.$vapi, win.ReaddyAssistant].filter(Boolean) as AnyRecord[];

    for (const target of candidates) {
      const send = target.sendMessage || target.send || target.addMessage;
      if (typeof send === "function") {
        try {
          (send as (payload: unknown) => void).call(target, {
            type: "add-message",
            message: { role: "user", content: question },
          });
          return true;
        } catch {
          /* try next */
        }
      }
    }
  } catch {
    /* ignore */
  }
  return false;
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Open the built-in chat widget (if not already open). */
export function openAiWidget() {
  const btn = document.querySelector("#vapi-widget-floating-button");
  if (btn) (btn as HTMLElement).click();
}

/**
 * Open the widget and best-effort send the given question.
 * Resolves when the attempt finishes (no throw).
 */
export async function askAiWidget(question: string): Promise<void> {
  const text = question.trim();
  if (!text) return;

  openAiWidget();

  if (tryNativeApi(text)) return;

  for (let attempt = 0; attempt < 25; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await delay(120);
    const input = findInput();
    if (!input) continue;

    try {
      input.focus();
      setNativeValue(input, text);
      await delay(80);
    } catch {
      /* ignore */
    }

    const sendBtn = findSendButton();
    if (sendBtn) {
      try {
        sendBtn.click();
        return;
      } catch {
        /* fall through to Enter */
      }
    }

    dispatchEnter(input);
    return;
  }
}